import { FormEvent, useEffect, useState } from 'react'
import { clientsApi, projectsApi } from '@/api/endpoints'
import type { Client, Project } from '@/api/types'
import { Button, Card, Field, Input, Select } from '@/components/ui'
import { formatMoney } from '@/utils/format'

export default function ProjectsPage() {
  const [projects, setProjects] = useState<Project[]>([])
  const [clients, setClients] = useState<Client[]>([])
  const [clientId, setClientId] = useState('')
  const [name, setName] = useState('')
  const [rate, setRate] = useState('0')
  const [currency, setCurrency] = useState('CZK')
  const [isBillable, setIsBillable] = useState(true)

  const load = () => projectsApi.list({ includeArchived: true }).then(setProjects)
  useEffect(() => {
    clientsApi.list().then(setClients)
    load()
  }, [])

  const create = async (e: FormEvent) => {
    e.preventDefault()
    if (!clientId || !name.trim()) return
    await projectsApi.create({
      clientId,
      name,
      isBillable,
      defaultRate: Number(rate) || 0,
      currency,
    })
    setName('')
    setRate('0')
    await load()
  }

  const toggleArchive = async (p: Project) => {
    await (p.archived ? projectsApi.unarchive(p.id) : projectsApi.archive(p.id))
    await load()
  }

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">Projekty</h1>
      <Card>
        <form onSubmit={create} className="flex flex-wrap items-end gap-3">
          <div className="min-w-44">
            <Field label="Klient">
              <Select value={clientId} onChange={(e) => setClientId(e.target.value)} required>
                <option value="">Vyber…</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <div className="min-w-40 flex-1">
            <Field label="Název">
              <Input value={name} onChange={(e) => setName(e.target.value)} required />
            </Field>
          </div>
          <div className="w-28">
            <Field label="Sazba">
              <Input type="number" min="0" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} />
            </Field>
          </div>
          <div className="w-20">
            <Field label="Měna">
              <Input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} maxLength={3} />
            </Field>
          </div>
          <label className="flex items-center gap-2 pb-2 text-sm">
            <input type="checkbox" checked={isBillable} onChange={(e) => setIsBillable(e.target.checked)} />
            Fakturovatelné
          </label>
          <Button type="submit">Přidat projekt</Button>
        </form>
      </Card>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-slate-400">
                <th className="py-2">Projekt</th>
                <th className="py-2">Klient</th>
                <th className="py-2 text-right">Sazba</th>
                <th className="py-2 text-center">Fakt.</th>
                <th className="py-2 text-right">Akce</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => (
                <tr key={p.id} className={`border-t border-slate-100 ${p.archived ? 'text-slate-400' : ''}`}>
                  <td className="py-2 font-medium">
                    {p.name}
                    {p.archived && <span className="ml-2 text-xs">(archiv)</span>}
                  </td>
                  <td className="py-2 text-slate-500">{p.clientName}</td>
                  <td className="py-2 text-right">{formatMoney(p.defaultRate, p.currency)}</td>
                  <td className="py-2 text-center">{p.isBillable ? '✓' : '—'}</td>
                  <td className="py-2 text-right">
                    <button className="text-xs text-brand-600 hover:underline" onClick={() => toggleArchive(p)}>
                      {p.archived ? 'obnovit' : 'archivovat'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}
